import * as React from "react";
import { Button as Base } from "rebass/styled-components";
import styled, { css } from "styled-components";

export interface IButtonStyledProps {
  disabled?: boolean;
  fullwidth?: string; // "true"
  variant?: string; //"primary" | "secondary" | "link" | "home";
  size?: string; // "sm" || "md" || "lg"
  hasicon: string; // "true" || "",
  justifyContent?: string;
  textTransform?: string;
}

const btnVariations = {
  baseBtn: css`
    display: inline-block;
    padding: ${({ theme }) => `${theme.space[3]} ${theme.space[4]}`};
    border: 0;
    background-color: ${({ theme }) => theme.colors.cyan};
    font-family: ${({ theme }) => theme.fonts.base};
    font-size: ${({ theme }) => theme.fontSizes[2]};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary};
    line-height: 1;
    border-radius: 8px;
    box-sizing: border-box;
    cursor: pointer;
    transition: all 250ms linear;
    text-decoration: none;

    &:hover {
      background-color: ${({ theme }) => theme.colors.cyanDark};
    }
  `,
  primary: css`
    background-color: ${({ theme }) => theme.colors.cyan};
    color: ${({ theme }) => theme.colors.primary};
    &:hover {
      background-color: ${({ theme }) => theme.colors.cyanDark};
    }
  `,
  secondary: css`
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.primary};
    &:hover {
      background-color: ${({ theme }) => theme.colors.greyLight};
    }
  `,

  tertiary: css`
    background-color: ${({ theme }) => theme.colors.white4};
    color: ${({ theme }) => theme.colors.white};
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.white16};
    &:hover {
      background-color: ${({ theme }) => theme.colors.white8};
    }
  `,

  home: css`
    width: 100%;
    height: 100%;
    background-color: transparent;

    &:hover {
      background-color: transparent;
    }
  `,
  link: css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.white};

    &:hover {
      background-color: transparent;
      color: ${({ theme }) => theme.colors.greyLight};
    }
  `,
  fullwidth: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  `,
  xs: css`
    font-size: 1.2rem;
    padding: 0.8rem;
  `,
  sm: css`
    font-size: 1.4rem;
    padding: 1rem;
  `,
  lg: css`
    padding: 1.6rem;
  `,
};

const flexVariations = {
  flexStart: css`
    justify-content: flex-start;
  `,
  flexEnd: css`
    justify-content: flex-end;
  `,
  spaceBetween: css`
    justify-content: space-between;
  `,
};

const StyledBtn = styled(Base)<IButtonStyledProps>`
  ${btnVariations.baseBtn};
  ${({ variant }) => variant === "primary" && btnVariations.primary};
  ${({ variant }) => variant === "secondary" && btnVariations.secondary};
  ${({ variant }) => variant === "tertiary" && btnVariations.tertiary};
  ${({ variant }) => variant === "link" && btnVariations.link};
  ${({ variant }) => variant === "home" && btnVariations.home};
  ${({ fullwidth }) => fullwidth === "true" && btnVariations.fullwidth}
  ${({ size }) => size === "xs" && btnVariations.xs}
  ${({ size }) => size === "sm" && btnVariations.sm}
  ${({ size }) => size === "lg" && btnVariations.lg}
  ${({ fullwidth, hasicon }) =>
    fullwidth === "true" && hasicon === "true" && flexVariations.spaceBetween};

  ${({ fullwidth, hasicon, justifyContent }) =>
    fullwidth === "true" &&
    hasicon === "true" &&
    justifyContent === "flexStart" &&
    flexVariations.flexStart};

  ${({ fullwidth, hasicon, justifyContent }) =>
    fullwidth === "true" &&
    hasicon === "true" &&
    justifyContent === "flexEnd" &&
    flexVariations.flexEnd}

  ${({ disabled, theme }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.colors.white4};
      box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.white16};
      color: ${theme.colors.white16};
      cursor: auto;

      &:hover {
        background-color: ${theme.colors.white4};
        cursor: not-allowed;
        pointer-events: all !important;
      }
    `}

    ${({ textTransform }) =>
    textTransform === "upperCase" &&
    css`
      text-transform: uppercase;
    `}
`;

interface IProps {
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  width?: number;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  ref?: React.RefObject<HTMLButtonElement>;
  children: React.ReactNode;
  disabled?: boolean;
  variant?: string;
  size?: string;
  // Need to clean this part up
  to?: string;
  href?: string;
  as?: any;
  tabIndex?: number;
  role?: string;
  target?: string;
  newTab?: boolean;
  rel?: string;
  justifyContent?: string;
  textTransform?: string;
  className?: string;
}

type Props = IProps;

const Button = React.forwardRef(
  (
    {
      as,
      width = 0,
      children,
      className,
      disabled = false,
      variant,
      type = "button",
      startIcon: startIconProp,
      endIcon: endIconProp,
      newTab = false,
      ...props
    }: Props,
    ref?: React.Ref<HTMLButtonElement>,
  ) => {
    const commonProps = {
      ref,
      disabled,
      variant,
      fullwidth: width === 1 ? "true" : "undefined",
      hasicon: !(startIconProp || endIconProp) ? "" : "true",
    };

    const startIcon = startIconProp && (
      <span style={{ marginInlineEnd: 8 }}>{startIconProp}</span>
    );

    const endIcon = endIconProp && (
      <span style={{ marginInlineStart: 8 }}>{endIconProp}</span>
    );

    // If it has a "href" prop we assume it's wants to be an anchor
    // Or it could be a Link component but you can use the prop.as for this as={Link}
    if (props.href || props.to) {
      if (disabled) {
        props.tabIndex = -1;
        props.role = "button";
      }

      if (newTab) {
        props.target = "_blank";
        props.rel = "noopener noreferrer";
      }

      return (
        <StyledBtn
          as={as || "a"}
          className={className}
          {...commonProps}
          {...props}
        >
          {startIcon}
          {children}
          {endIcon}
        </StyledBtn>
      );
    }

    return (
      <StyledBtn className={className} type={type} {...commonProps} {...props}>
        {startIcon}
        {children}
        {endIcon}
      </StyledBtn>
    );
  },
);

export default Button;
